import { MAX_LOOKALIKE_DOMAINS } from "@/pages/Private/pages/CompanyEnrich/schema/companyEnrich";
import { IcpPosition } from "@/pages/Private/pages/List/pages/ListEditDetails/enum/icp.enum";

import {
	isCompanySourced,
	LeadDbConfig,
	NewInRoleConfig,
	OnboardingListConfig,
	PeopleAtCompanyConfig,
	TopicConfig,
} from "./onboardingListTypes";

/** The company search caps the semantic query (`CompanySearchPreviewDto`). */
export const MAX_SEMANTIC_QUERY_LENGTH = 500;

/** The config form fields that can carry a validation message. */
export type ConfigFieldKey =
	| "hiringFor"
	| "location"
	| "findTitles"
	| "similarTo"
	| "keywords"
	| "topic"
	| "semanticQuery"
	| "capture"
	| "changeTypes"
	| "newInRoleFilters"
	| "businessType"
	| "country"
	| "aiQuery"
	| "fundingFilters"
	| "peopleAtCompany";

/** field → i18n key under `onboarding.lists.config.errors`. */
export type ConfigFieldErrors = Partial<Record<ConfigFieldKey, string>>;

/** A list is only worth creating when it narrows the source down at all. */
const hasAnyLeadDbFilter = (config: LeadDbConfig): boolean =>
	config.positionQuery.length > 0 ||
	config.seniority.length > 0 ||
	config.department.length > 0 ||
	config.countries.length > 0 ||
	config.industries.length > 0 ||
	config.employees.length > 0 ||
	config.companyCountries.length > 0 ||
	config.keywords.length > 0 ||
	config.semanticQuery.trim().length > 0;

const hasAnyNewInRoleFilter = (config: NewInRoleConfig): boolean =>
	config.seniority.length > 0 ||
	config.department.length > 0 ||
	config.countries.length > 0 ||
	config.industries.length > 0 ||
	config.employees.length > 0;

/** The semantic query is free text, so its only rule is the upstream length cap. */
const semanticQueryError = (semanticQuery: string): ConfigFieldErrors | null =>
	semanticQuery.trim().length > MAX_SEMANTIC_QUERY_LENGTH
		? { semanticQuery: "maxSemanticQuery" }
		: null;

const hasAnyCapture = ({ capture }: TopicConfig): boolean =>
	capture.posts || capture.likes || capture.comments;

/**
 * The search profile is validated by the rule the in-app details step applies
 * (`useIcpValidation`): a custom title needs a title, department & seniority
 * needs at least one of the two. "CEO and similar" needs no input at all.
 */
const peopleAtCompanyErrorsFor = (people: PeopleAtCompanyConfig): ConfigFieldErrors => {
	if (people.position === IcpPosition.CUSTOM && people.title.trim().length === 0) {
		return { peopleAtCompany: "required" };
	}

	if (
		people.position === IcpPosition.DEPARTMENT_SENIORITY &&
		people.seniority.length === 0 &&
		people.department.length === 0
	) {
		return { peopleAtCompany: "requiredAnyFilter" };
	}

	return {};
};

/**
 * Validates a config against the **same** rules the in-app create flows apply,
 * so an onboarding list is never weaker than a manually built one: JOBS needs a
 * query + a location (`CreateJobForm`), LOOK_A_LIKE needs 1…10 seed domains and
 * NEW_IN_ROLE a change type + window (`companyEnrich` schema), BY_TOPIC needs a
 * query and at least one captured engagement (`liPost` schema), MAPS a keyword
 * (`map` schema) and PARALLEL_AI a query (`parallelAi` schema).
 *
 * On top of those, every filter-only source must narrow its source down at
 * least once — onboarding never creates an "everything" list.
 */
export const configErrorsFor = (config: OnboardingListConfig): ConfigFieldErrors =>
	isCompanySourced(config)
		? { ...sourceErrorsFor(config), ...peopleAtCompanyErrorsFor(config.peopleAtCompany) }
		: sourceErrorsFor(config);

/** The errors of the source filters themselves, without the search profile. */
const sourceErrorsFor = (config: OnboardingListConfig): ConfigFieldErrors => {
	switch (config.kind) {
		case "hiring": {
			const errors: ConfigFieldErrors = {};

			if (config.query.trim().length < 2) {
				errors.hiringFor = "required";
			}

			if (config.location === null) {
				errors.location = "required";
			}

			return errors;
		}

		case "lookalike":
			if (config.domains.length === 0) {
				return { similarTo: "required" };
			}

			if (config.domains.length > MAX_LOOKALIKE_DOMAINS) {
				return { similarTo: "maxDomains" };
			}

			return semanticQueryError(config.semanticQuery) ?? {};

		case "leadDb": {
			const semantic = semanticQueryError(config.semanticQuery);

			if (semantic) {
				return semantic;
			}

			return hasAnyLeadDbFilter(config) ? {} : { findTitles: "requiredAnyFilter" };
		}

		case "newInRole": {
			const errors: ConfigFieldErrors = {};

			if (config.changeTypes.length === 0) {
				errors.changeTypes = "required";
			}

			if (!hasAnyNewInRoleFilter(config)) {
				errors.newInRoleFilters = "requiredAnyFilter";
			}

			return errors;
		}

		case "topic": {
			const errors: ConfigFieldErrors = {};

			if (config.query.trim().length < 2) {
				errors.topic = "required";
			}

			if (!hasAnyCapture(config)) {
				errors.capture = "required";
			}

			return errors;
		}

		case "gmaps": {
			const errors: ConfigFieldErrors = {};

			if (config.query.trim().length < 2) {
				errors.businessType = "required";
			}

			if (config.country.trim().length < 2) {
				errors.country = "required";
			}

			return errors;
		}

		case "aiCompany":
		case "aiNews":
			return config.query.trim().length < 2 ? { aiQuery: "required" } : {};

		case "funding":
		case "mna":
			return config.countries.length === 0 && config.category.length === 0
				? { fundingFilters: "requiredAnyFilter" }
				: {};
	}
};
